import each from 'lodash-es/each';
import isObject from 'lodash-es/isObject';
import last from 'lodash-es/last';

/**
 * Represents a serializer interface.
 */
export class ISerializer {
    /**
     * Serializes the given data.
     * @param data The data to serialize.
     * @returns The serialized data as a number or string.
     */
    static serialize(data: any): number | string {
        return '';
    }

    /**
     * Unserializes the given data string.
     * @param dataString The data string to unserialize.
     * @returns The unserialized data.
     */
    static unserialize(dataString: string): any {}
}

export class StandardSerializer implements ISerializer {
    static separator = '&';
    static equals = '=';
    /**
     * Serializes the given data.
     * @param data The data to serialize.
     * @param prefix The prefix to use for each property.
     * @param target The target object to store the serialized data.
     * @param ops The options for serialization.
     * @returns The serialized data as a number or string.
     */
    static serialize(data: any, prefix: string = '', target?: any, ops?: { noescape: boolean }): number | string {
        switch (typeof data) {
            case 'string':
                return ops && ops.noescape ? <string>data : <string>encodeURIComponent(data);
            case 'number':
                return <number>data;
            case 'object':
                if (data === null) {
                    return '';
                }

                if (data.length) {
                    return (<any[]>data).map(item => this.serialize(item, prefix, undefined, ops)).join(',');
                }
                const _target = target || [];
                each(data, (prop, key) => {
                    const val = this.serialize(prop, prefix + key + '.', _target, ops);

                    if (val) {
                        _target.push(prefix + key + '=' + val);
                    }
                    return undefined;
                });

                //  if there is target from parent then do not return anything - we dont want it twice in string
                if (target) {
                    return '';
                }
                return _target.join('&');
            case 'undefined':
                return '';
            default:
                console.error('unsoported type for serialization', data, new Error().stack);
        }
        return '';
    }
    /**
     * Unserializes the given data string.
     * @param dataString The data string to unserialize.
     * @param ops The options for unserialization.
     * @returns The unserialized data.
     */
    static unserialize(dataString: string, ops?: { noescape: boolean }): any {
        if (!dataString) {
            return undefined;
        }
        // flat data no objects
        if (dataString.indexOf('=') === -1) {
            dataString = ops && ops.noescape ? dataString : decodeURIComponent(dataString);

            // if contains non digits then string
            if (dataString.match(/[^\d\.]/)) {
                // if there is coma then its an array
                if (dataString.indexOf(',') !== -1) {
                    return dataString.split(',').map(part => this.unserialize(part, ops));
                }
                // othwrwise plain string
                return dataString;
            }

            // if contains dot then float
            if (dataString.indexOf('.') !== -1) {
                return parseFloat(dataString);
            }
            // otherwise int
            return parseInt(dataString, 10);
        } else {
            // object
            const obj: any = {};
            const pattern = /([&\?]|^)([^&=]+?)=([^=]*?)$/;
            let s = dataString;
            let match;
            /**
             * OK, so this logic is little bit twisted
             * Imagine query like this:
             * filters.book_subject=Books,Food&Drink Books&filters.price=20,123
             * see that there is & in Food&Drink Books and any standard approach is to first split by & and then by =
             * We cant do that, so we match shortest property=value pairs (starting from the end, therefore the replace)
             *
             */
            let protection = 0;
            // tslint:disable-next-line: no-conditional-assignment
            while ((match = s.match(pattern))) {
                const name = match[2];
                const value = match[3];
                const parts = name.split('.');
                let tmp = obj;
                for (let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if (!tmp[part] || !isObject(tmp[part])) {
                        tmp[part] = {};
                    }
                    tmp = tmp[part];
                }
                tmp[<string>last(parts)] = StandardSerializer.unserialize(value, ops);
                s = s.replace(pattern, '');
                if (protection++ > 200) {
                    // for safety
                    break;
                }
            }

            return obj;
        }
    }
}
